import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import "react-toastify/dist/ReactToastify.css";
import { formatDateReadableWithDayOfWeekNoYear } from "../../requests/utilities/helpers";
import { formatDateReadableWithDayOfWeek } from "../../requests/utilities/helpers";

function EventsListModal(props) {

  // Convert 24-hour time (HH:MM) to 12-hour format (h:MMam/pm)
  const formatTime = (time24) => {
    if (!time24 || time24 === "TBA" || time24 === "TBD") return time24;

    const [hours, minutes] = time24?.split(':').map(Number);
    const period = hours >= 12 ? 'pm' : 'am';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for midnight

    return `${hours12}:${minutes?.toString()?.padStart(2, '0')}${period}`;
  };
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 550,
    maxHeight: 800,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  };

  const categoryPriority = {
    festival: 0,
    parade: 0,
    civic: 0,
    transportation: 0,
    sports: 1,
    conference: 2,
    concert: 3,
    other: 4,
  };

  const filtered_eventsList = [...props.eventsList].sort((a, b) => {
    const priorityA = categoryPriority[a.event_type] ?? categoryPriority.other;
    const priorityB = categoryPriority[b.event_type] ?? categoryPriority.other;
    return priorityA - priorityB;
  });


  return (
    <>
      <Modal
        open={props.open}
        onClose={() => props.handleClose()}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box sx={style}>
          <div
            className="absolute right-2 top-2 text-gray-500 cursor-pointer"
            onClick={() => props.handleClose()}
          >
            <CloseIcon />
          </div>
          <div>
            <div className="pb-5 border-b border-slate-200">
              <h3 className="text-2xl text-slate-900">Local Events</h3>
              <p className="text-slate-500 mt-2">
                {formatDateReadableWithDayOfWeek(props.date_str)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Context only — these signals do not change the forecast.
              </p>
            </div>
            <div>
              {filtered_eventsList.map((event, index) => (
                <div
                  key={`${event.origin || "local_event"}-${event.uuid || event.id || index}`}
                  className="py-3 border-b border-slate-200"
                >
                  <div>
                    {/* Event title */}
                    <h4 className="text-slate-900 text-base font-semibold">
                      {event.event_name}
                    </h4>
                  </div>

                  {/* Event details - always shown */}
                  <div className="mt-3 bg-slate-50 rounded-lg p-3 space-y-2">
                    {/* Event time and venue */}
                    {(
                      event.all_day
                      || (event.start_time && event.start_time !== "TBA" && event.start_time !== "TBD")
                      || event.venue_name
                      || event.location_name
                      || event.location
                    ) && (
                      <div className="flex items-center gap-2 text-sm">
                        <AccessTimeIcon style={{ fontSize: "16px" }} className="text-slate-600" />
                        <span className="text-slate-700">
                          {event.all_day
                            ? "All day"
                            : event.start_time && event.start_time !== "TBA" && event.start_time !== "TBD"
                              ? `${formatTime(event.start_time)}${event.end_time && event.end_time !== "TBA" && event.end_time !== "TBD" ? ` - ${formatTime(event.end_time)}` : ''}`
                              : "Time TBD"}
                          {(event.venue_name || event.location_name || event.location) && ` @ ${event.venue_name || event.location_name || event.location}`}
                        </span>
                      </div>
                    )}

                    {/* Expected attendance */}
                    {event.estimated_attendance && (
                      <div className="flex items-center gap-2 text-sm">
                        <PersonIcon style={{ fontSize: "16px" }} className="text-slate-600" />
                        <span className="text-slate-700">
                          ~{event.estimated_attendance?.toLocaleString()} expected attendees
                        </span>
                      </div>
                    )}

                    {/* Event URL link */}
                    {event.event_url && (
                      <div className="mt-2">
                        <a
                          href={event.event_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-violet-700 hover:text-violet-800 text-sm underline flex items-center gap-1"
                        >
                          View event details
                          <OpenInNewIcon style={{ fontSize: "14px" }} />
                        </a>
                      </div>
                    )}

                  </div>

                  {/* Badges row */}
                  <div className="w-full flex items-center justify-between mt-3">
                    {event.distance != null ? (
                      <p className="text-xs px-3 py-0.5 rounded-full bg-slate-50 text-slate-600 border border-slate-300">
                        {event.distance} miles away
                      </p>
                    ) : (
                      <span />
                    )}
                    <div className="flex gap-2">
                      {/* Event type badge */}
                      {event.event_type && (
                        <p className="text-xs px-3 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-300 capitalize">
                          {event.event_type}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="w-full flex justify-end mt-8">
              <div className="flex space-x-3">
                <div
                  className="px-8 py-2 bg-white border border-violet-700 text-violet-700 hover:bg-slate-100 rounded-full text-base w-fit cursor-pointer"
                  onClick={() => props.handleClose()}
                >
                  Close
                </div>
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}

export default EventsListModal;
